import { IconButton, Tooltip } from '@mui/material'
import {
  GridCellParams,
  GridColDef,
  GridColumnResizeParams,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowId,
  GridRowModel,
} from '@mui/x-data-grid-pro'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { updateEvaluationFileItem } from 'src/screens/shared/evidence-of-effectiveness/utils/EvidenceOfEffectivenessUtils'
import { FileService } from 'src/service/axios/FileService'
import { NumberInput } from 'src/shared/form/grid/NumberInput'
import { TextInput } from 'src/shared/form/grid/TextInput'
import { DataGridCustomFooter } from 'src/shared/form/grid/footer/DataGridCustomFooter'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { DeleteIcon, DownloadIcon, ErrorOutlineIcon, InfoIcon } from 'src/shared/icons/Icons'
import { S } from 'src/shared/styled/S'
import { DataGridUtils } from 'src/shared/utils/DataGridUtils'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'

export interface EvaluationFileItem {
  id: number
  file: File
  fileUUID: string | null
  fileName: string
  entityId: number | null
  evaluationIndex: number
  name: string
  author: string
  yearPublication: number | null
  publish: boolean
}

interface Props {
  editMode: boolean
  helperText?: string
  rows: EvaluationFileItem[]
  onDeleteFile?: (id: number) => void
  onUpdateFileItem?: (updatedItem: EvaluationFileItem) => void
  showDownloadButton?: boolean
  showDeleteButton?: boolean
  showPublishCheckbox?: boolean
  showHeaderTooltip?: boolean
  showHeaderRequiredAsterisk?: boolean
  canDownloadFile?: (fileItem: EvaluationFileItem) => boolean
  loading: boolean
  error?: boolean
  errorMessage?: string
}

export const EvidenceOfEffectivenessFilesGrid = ({
  editMode,
  helperText,
  rows,
  onDeleteFile,
  loading,
  onUpdateFileItem,
  canDownloadFile,
  showDownloadButton = false,
  showDeleteButton = false,
  showPublishCheckbox = false,
  showHeaderTooltip = true,
  showHeaderRequiredAsterisk = false,
  error = false,
  errorMessage,
}: Props): ReactElement => {
  const { getMessage } = useMessageSource()
  const notificationService = useNotificationService()
  const gridTranslations = useGridTranslateHook()

  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(null)
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})

  const processRowUpdate = useCallback(
    (newRow: GridRowModel) => {
      const originalItem = rows.find((f) => f.id === newRow.id)

      if (!originalItem) return newRow

      const updatedItem = updateEvaluationFileItem(originalItem, newRow)

      if (onUpdateFileItem) {
        onUpdateFileItem(updatedItem)
      }

      return newRow
    },
    [rows, onUpdateFileItem],
  )

  const handleColumnResize = useCallback((params: GridColumnResizeParams) => {
    setColumnWidths((prev) => ({
      ...prev,
      [params.colDef.field]: params.width,
    }))
  }, [])

  const handleCellClick = useCallback(
    (params: GridCellParams) => {
      if (!params.isEditable || !editMode) {
        return
      }

      // For boolean fields, toggle immediately
      if (params.colDef.type === 'boolean') {
        const updatedRow = { ...params.row, [params.field]: !params.value }
        processRowUpdate(updatedRow)
      }
    },
    [editMode, processRowUpdate],
  )

  const handleDownloadFile = useCallback(
    async (fileItem: EvaluationFileItem) => {
      const { fileUUID, id, evaluationIndex, fileName } = fileItem

      if (!fileUUID) {
        notificationService.operationFailed()
        return
      }

      setDownloadingFileId(id)

      try {
        await FileService.downloadWithFileName(fileUUID, `${evaluationIndex}_${fileName}`)
      } catch (e) {
        notificationService.operationFailed()
      } finally {
        setDownloadingFileId(null)
      }
    },
    [notificationService],
  )

  const onRifmChange = useCallback((api: GridRenderEditCellParams['api'], id: GridRowId, field: string, value: any) => {
    api.setEditCellValue({ id, field, value })
  }, [])

  const renderPlaceholderCell = useCallback(
    (value: any): ReactElement | string => {
      if (DataGridUtils.isEmptyValue(value)) {
        return <span style={{ opacity: 0.42, fontStyle: 'italic' }}>{getMessage('label.section.not.available')}</span>
      }
      return value ?? ''
    },
    [editMode, getMessage],
  )

  const renderHeaderWithoutTooltip = useCallback(
    (headerTitleKey: string, isRequiredColumn: boolean = true): string | undefined => {
      if (showHeaderTooltip === true) {
        return undefined
      }

      const titleText = getMessage(headerTitleKey)

      if (showHeaderRequiredAsterisk && isRequiredColumn) {
        return titleText + ' *'
      }

      return titleText
    },
    [getMessage, showHeaderRequiredAsterisk, showHeaderTooltip],
  )

  const renderHeaderWithTooltip = useCallback(
    (headerTitleKey: string, tooltipKey: string, isRequiredColumn: boolean = true): ReactElement | undefined => {
      if (showHeaderTooltip === false || showHeaderTooltip === undefined) {
        return undefined
      }

      let titleText = ''

      if (showHeaderRequiredAsterisk && isRequiredColumn) {
        titleText = getMessage(headerTitleKey) + ' *'
      } else {
        titleText = getMessage(headerTitleKey)
      }

      return (
        <>
          {titleText}
          <Tooltip placement="right" title={getMessage(tooltipKey)}>
            <IconButton
              size="small"
              color={'inherit'}
              sx={{
                ml: 1,
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )
    },
    [getMessage, showHeaderRequiredAsterisk, showHeaderTooltip],
  )

  const columns: GridColDef[] = useMemo(() => {
    const col: GridColDef[] = [
      {
        field: 'evaluationIndex',
        flex: columnWidths.evaluationIndex ? undefined : 1,
        width: columnWidths.evaluationIndex,
        minWidth: 50,
        sortable: !editMode,
        editable: false,
        filterable: !editMode,
        disableColumnMenu: editMode,
        pinnable: !editMode,
        headerName: '#',
        renderCell: (params: GridRenderCellParams) => params.value,
      },
      {
        field: 'name',
        flex: columnWidths.name ? undefined : 2,
        width: columnWidths.name,
        minWidth: 200,
        sortable: !editMode,
        editable: editMode,
        filterable: !editMode,
        disableColumnMenu: editMode,
        pinnable: !editMode,
        renderHeader: () =>
          renderHeaderWithTooltip(
            'label.evidence-of-effectiveness.header.name',
            'label.evidence-of-effectiveness.header.name.tooltip',
          ),
        headerName: renderHeaderWithoutTooltip('label.evidence-of-effectiveness.header.name'),
        renderCell: (params: GridRenderCellParams<EvaluationFileItem>) => {
          const hasFile = !!params.row.fileUUID

          return (
            <>
              {!hasFile && (
                <Tooltip placement="right" title={getMessage('label.file-not-saved.tooltip')}>
                  <IconButton
                    size="small"
                    color={'inherit'}
                    sx={{
                      mr: 1,
                    }}
                  >
                    <ErrorOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {renderPlaceholderCell(params.value)}
            </>
          )
        },
        renderEditCell: (params: GridRenderEditCellParams) => {
          const { id, field, api, value } = params

          return (
            <TextInput
              value={value}
              placeholder={getMessage('label.section.not.available')}
              onChange={(val) => onRifmChange(api, id, field, val)}
              error={DataGridUtils.isEmptyValue(value)}
            />
          )
        },
      },
      {
        field: 'yearPublication',
        minWidth: 200,
        flex: columnWidths.yearPublication ? undefined : 1,
        width: columnWidths.yearPublication,
        editable: editMode,
        sortable: !editMode,
        disableColumnMenu: editMode,
        filterable: !editMode,
        pinnable: !editMode,
        align: 'left',
        headerAlign: 'left',
        type: 'number',
        renderHeader: () =>
          renderHeaderWithTooltip(
            'label.evidence-of-effectiveness.header.yearPublication',
            'label.evidence-of-effectiveness.header.yearPublication.tooltip',
          ),
        headerName: renderHeaderWithoutTooltip('label.evidence-of-effectiveness.header.yearPublication'),
        renderCell: (params: GridRenderCellParams) => renderPlaceholderCell(params.value),
        valueFormatter: (params) => {
          if (Number.isNaN(params) || params === null || params === undefined || !params) {
            return ''
          }
          return params
        },

        renderEditCell: (params: GridRenderEditCellParams) => {
          const { id, field, api, value } = params

          return (
            <NumberInput
              value={value}
              placeholder={getMessage('label.section.not.available')}
              shouldFormatInteger={false}
              onChange={(val) => onRifmChange(api, id, field, val)}
              error={DataGridUtils.isInvalidNumberValue(value) || !DataGridUtils.isValidYear(value)}
            />
          )
        },
      },
      {
        field: 'author',
        flex: columnWidths.author ? undefined : 2,
        width: columnWidths.author,
        minWidth: 200,
        editable: editMode,
        sortable: !editMode,
        filterable: !editMode,
        disableColumnMenu: editMode,
        pinnable: !editMode,
        renderHeader: () =>
          renderHeaderWithTooltip(
            'label.evidence-of-effectiveness.header.author',
            'label.evidence-of-effectiveness.header.author.tooltip',
          ),
        headerName: renderHeaderWithoutTooltip('label.evidence-of-effectiveness.header.author'),
        renderCell: (params: GridRenderCellParams) => renderPlaceholderCell(params.value),
        renderEditCell: (params: GridRenderEditCellParams) => {
          const { id, field, api, value } = params
          return (
            <TextInput
              value={value}
              placeholder={getMessage('label.section.not.available')}
              onChange={(val) => onRifmChange(api, id, field, val)}
              error={DataGridUtils.isEmptyValue(value)}
            />
          )
        },
      },
    ]

    // publish column checkbox
    if (showPublishCheckbox) {
      col.push({
        field: 'publish',
        renderHeader: () =>
          renderHeaderWithTooltip(
            'label.evidence-of-effectiveness.header.publication-authorized',
            'label.evidence-of-effectiveness.header.publication-authorized.tooltip',
          ),
        headerName: renderHeaderWithoutTooltip('label.evidence-of-effectiveness.header.publication-authorized'),
        flex: columnWidths.publish ? undefined : 2,
        width: columnWidths.publish,
        minWidth: 200,
        type: 'boolean',
        editable: editMode,
        disableColumnMenu: editMode,
        sortable: !editMode,
        filterable: !editMode,
        pinnable: !editMode,
        headerAlign: 'center',
        align: 'center',
      })
    }

    // uploaded file name column
    col.push({
      field: 'fileName',
      minWidth: 200,
      flex: columnWidths.fileName ? undefined : 2,
      width: columnWidths.fileName,
      pinnable: !editMode,
      editable: false,
      sortable: !editMode,
      filterable: !editMode,
      renderHeader: () =>
        renderHeaderWithTooltip(
          'label.evidence-of-effectiveness.header.uploaded-file-name',
          'label.evidence-of-effectiveness.header.uploaded-file-name.tooltip',
          false,
        ),
      headerName: renderHeaderWithoutTooltip('label.evidence-of-effectiveness.header.uploaded-file-name', false),
    })

    // action column
    if (showDeleteButton || showDownloadButton) {
      col.push({
        field: 'actions',
        headerName: getMessage('label.actions'),
        minWidth: 100,
        width: columnWidths.actions,
        sortable: false,
        filterable: false,
        pinnable: false,
        editable: false,
        disableColumnMenu: true,
        headerAlign: 'right',
        align: 'right',
        renderCell: (params: GridRenderCellParams<EvaluationFileItem>) => {
          if (showDeleteButton) {
            return (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  if (onDeleteFile) {
                    onDeleteFile(params.row.id)
                  }
                }}
                color="primary"
                size="small"
                aria-label={getMessage('button.delete')}
              >
                <DeleteIcon />
              </IconButton>
            )
          }

          if (showDownloadButton && (canDownloadFile ? canDownloadFile(params.row) : true)) {
            const isDownloading = downloadingFileId === params.row.id

            return (
              <IconButton
                onClick={() => handleDownloadFile(params.row)}
                color="primary"
                size="small"
                loading={isDownloading}
                aria-label={getMessage('button.download')}
              >
                <DownloadIcon />
              </IconButton>
            )
          }
        },
      })
    }

    return col
  }, [
    columnWidths.name,
    columnWidths.author,
    columnWidths.yearPublication,
    columnWidths.fileName,
    columnWidths.publish,
    columnWidths.actions,
    editMode,
    renderHeaderWithoutTooltip,
    showPublishCheckbox,
    showDeleteButton,
    showDownloadButton,
    renderHeaderWithTooltip,
    onRifmChange,
    getMessage,
    canDownloadFile,
    onDeleteFile,
    downloadingFileId,
    handleDownloadFile,
  ])

  const footerSlot = useMemo(
    () => ({
      footer: (props: any) => (
        <DataGridCustomFooter {...props} helperText={helperText} error={error} errorMessage={errorMessage} />
      ),
    }),
    [helperText, error, errorMessage],
  )

  return (
    <DataGridContainer error={error}>
      <S.DataGrid.ViewEditMode
        $viewMode={!editMode}
        rows={rows}
        columns={columns}
        onCellClick={handleCellClick}
        onColumnResize={handleColumnResize}
        processRowUpdate={processRowUpdate}
        hideFooter={false}
        disableColumnReorder
        loading={loading}
        disableRowSelectionOnClick
        disableColumnSelector
        localeText={gridTranslations}
        slots={footerSlot}
        sx={{
          ...(editMode && {
            '& .MuiDataGrid-cell--editable': {
              cursor: 'pointer',
            },
          }),
        }}
      />
    </DataGridContainer>
  )
}
